import { useEffect, useMemo, useState } from "react";
import { Eye, Users as UsersIcon, TrendingUp, Smartphone, Loader2 } from "lucide-react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart,
} from "recharts";
import { adminAnalyticsSummary } from "../../lib/adminApi";

const RANGE_OPTIONS = [
    { days: 7, label: "7 dage" },
    { days: 30, label: "30 dage" },
    { days: 90, label: "90 dage" },
];

function formatShortDate(iso) {
    try {
        const [, m, d] = iso.split("-");
        return `${parseInt(d, 10)}/${parseInt(m, 10)}`;
    } catch { return iso; }
}

function NumberCard({ icon: Icon, label, value, sub, accent }) {
    return (
        <div className={`rounded-xl border border-slate-200 bg-white p-4`}>
            <div className="flex items-center gap-2">
                <span className={`h-8 w-8 rounded-md inline-flex items-center justify-center ${accent}`}>
                    <Icon className="h-4 w-4 text-white" />
                </span>
                <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</span>
            </div>
            <div className="mt-3 font-display text-2xl font-bold text-slate-900">{value}</div>
            {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
        </div>
    );
}

function HorizontalBars({ items, valueKey, labelKey, max }) {
    if (!items?.length) {
        return <div className="text-sm text-slate-400">Ingen data endnu</div>;
    }
    const peak = max || Math.max(...items.map((x) => x[valueKey]));
    return (
        <ul className="space-y-2.5">
            {items.map((row) => (
                <li key={row[labelKey]} className="flex items-center gap-3 text-sm">
                    <span className="w-44 truncate font-medium text-slate-700" title={row[labelKey]}>
                        {row[labelKey] || "—"}
                    </span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-sky-400 to-sky-600"
                            style={{ width: peak > 0 ? `${(row[valueKey] / peak) * 100}%` : "0%" }}
                        />
                    </div>
                    <span className="w-12 text-right tabular-nums text-slate-600">{row[valueKey]}</span>
                </li>
            ))}
        </ul>
    );
}

export default function AnalyticsPanel() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        adminAnalyticsSummary(days)
            .then((d) => { if (!cancelled) setData(d); })
            .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Kunne ikke hente"); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [days]);

    const chartData = useMemo(
        () => (data?.daily || []).map((d) => ({ ...d, label: formatShortDate(d.date) })),
        [data]
    );

    if (loading && !data) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Indlæser besøgsstatistik…
            </div>
        );
    }
    if (error) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-700 text-sm">
                Kunne ikke indlæse statistik: {error}
            </div>
        );
    }
    if (!data) return null;

    const t = data.totals;
    return (
        <section className="space-y-5" data-testid="admin-analytics">
            <header className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display text-xl font-bold text-slate-900">Besøgsstatistik</h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Cookieless · samme-dag-anonymiseret hash · GDPR uden samtykke
                    </p>
                </div>
                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 text-sm">
                    {RANGE_OPTIONS.map((r) => (
                        <button
                            key={r.days}
                            type="button"
                            onClick={() => setDays(r.days)}
                            data-testid={`analytics-range-${r.days}`}
                            className={`px-3 py-1 rounded-md font-semibold transition-colors ${
                                days === r.days
                                    ? "bg-sky-600 text-white"
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </header>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <NumberCard icon={Eye} label="Besøg i dag" value={t.views_today}
                    sub={`${t.visitors_today} unikke`} accent="bg-sky-600" />
                <NumberCard icon={Eye} label="Besøg 7 dage" value={t.views_7}
                    sub={`${t.visitors_7} unikke`} accent="bg-sky-500" />
                <NumberCard icon={UsersIcon} label={`Unikke ${days}d`} value={t.visitors_30}
                    sub={`${t.views_30} total visninger`} accent="bg-violet-600" />
                <NumberCard icon={TrendingUp} label="Konvertering" value={`${t.conversion_pct}%`}
                    sub={`${t.signups_30} tilmeldinger`} accent="bg-emerald-600" />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Besøg pr. dag</h3>
                <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                            <defs>
                                <linearGradient id="visitsGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.35} />
                                    <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                            <XAxis dataKey="label" stroke="#94a3b8" tick={{ fontSize: 11 }}
                                interval={Math.max(0, Math.floor(chartData.length / 10))} />
                            <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} allowDecimals={false} />
                            <Tooltip
                                contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                                labelStyle={{ color: "#475569", fontWeight: 600 }}
                            />
                            <Area type="monotone" dataKey="views" stroke="#0284c7" strokeWidth={2}
                                fill="url(#visitsGrad)" name="Besøg" />
                            <Line type="monotone" dataKey="visitors" stroke="#7c3aed" strokeWidth={2}
                                dot={false} name="Unikke" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Top sider</h3>
                    <HorizontalBars items={data.top_paths} valueKey="views" labelKey="path" />
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Top kilder</h3>
                    <HorizontalBars items={data.top_referrers} valueKey="views" labelKey="referrer" />
                </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                    <Smartphone className="h-4 w-4" /> Enheder
                </h3>
                <div className="flex gap-6 flex-wrap">
                    {(data.devices || []).map((d) => {
                        const total = data.totals.views_30 || 1;
                        const pct = ((d.views / total) * 100).toFixed(0);
                        const label = { mobile: "Mobil", tablet: "Tablet", desktop: "Desktop" }[d.device] || d.device;
                        return (
                            <div key={d.device} className="flex flex-col">
                                <span className="text-2xl font-display font-bold text-slate-900">{pct}%</span>
                                <span className="text-xs text-slate-500">{label} · {d.views} besøg</span>
                            </div>
                        );
                    })}
                    {!data.devices?.length && (
                        <span className="text-sm text-slate-400">Ingen data endnu</span>
                    )}
                </div>
            </div>
        </section>
    );
}
